import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import './HeroStage.css';

/* DossierProof animated hero stage (HH design system handoff).
   Document cards fly in, get "read" (scan sweep), drop into the dossier folder
   while the counter climbs 0→14, then risk flags surface. Continuous loop.
   Presentational + self-contained (sample data baked in, per the handoff).
   Falls back to the static assembled end-state under prefers-reduced-motion. */

const DOCS = [
  { type: 'Leasehold deed', name: 'Erfpachtakte',      lines: 3 },
  { type: 'VvE minutes',    name: 'Notulen ALV 2025',   lines: 4 },
  { type: 'NEN 2580',       name: 'Meetrapport',        lines: 2 },
  { type: 'WOZ assessment', name: 'WOZ-beschikking',    lines: 3 },
  { type: 'Energy label',   name: 'Energielabel A',     lines: 2 },
];

const FLAGS = [
  { sev: 'red',   label: 'Leasehold buy-off expires', ref: 'p.4' },
  { sev: 'amber', label: 'NEN 2580 area mismatch',    ref: 'p.2' },
  { sev: 'green', label: 'VvE reserve fund healthy',  ref: 'p.9' },
];

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

export default function HeroStage() {
  const sceneRef = useRef(null);
  const dossierRef = useRef(null);
  const phaseRef = useRef(null);
  const countRef = useRef(null);
  const kFlagsRef = useRef(null);
  const docRefs = useRef([]);
  const flagRefs = useRef([]);

  // Skeleton line widths — fixed once so re-renders don't reshuffle them.
  const lineWidths = useMemo(
    () => DOCS.map(d => Array.from({ length: d.lines }, () => 60 + Math.random() * 38)),
    [],
  );

  useLayoutEffect(() => {
    const reduce = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scene = sceneRef.current;
    const docEls = docRefs.current.filter(Boolean);
    const flagEls = flagRefs.current.filter(Boolean);
    const dossier = dossierRef.current;
    if (!scene || !docEls.length) return;

    let W = scene.clientWidth;
    const centerX = w => W / 2 - w / 2;
    const flagPositions = () => ([
      { x: W / 2 - 150, y: 150 },
      { x: W / 2 + 18,  y: 120 },
      { x: W / 2 + 30,  y: 250 },
    ]);
    const setCount = n => { if (countRef.current) countRef.current.textContent = String(n); };
    const setKFlags = n => { if (kFlagsRef.current) kFlagsRef.current.textContent = String(n); };

    // Static assembled end-state for reduced motion / no GSAP.
    if (reduce || !gsap) {
      setCount(14);
      setKFlags(3);
      const pos = flagPositions();
      flagEls.forEach((f, i) => { f.style.left = pos[i].x + 'px'; f.style.top = pos[i].y + 'px'; f.style.opacity = 1; });
      gsap && gsap.set(docEls, { opacity: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      W = scene.clientWidth;
      gsap.set(docEls, { opacity: 0 });
      gsap.set(flagEls, { opacity: 0 });
      setCount(0);
      setKFlags(0);

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.4, defaults: { ease: 'power3.out' } });

      // phase 1 — documents fly in, get read, drop into the dossier
      tl.call(() => { if (phaseRef.current) phaseRef.current.textContent = 'reading documents'; });
      docEls.forEach((el, i) => {
        const startX = (i % 2 === 0) ? -160 : W + 10;
        const startY = -40 + i * 8;
        const restY = 150 - i * 4;
        const restRot = (i - 2) * 2.2;
        gsap.set(el, { x: startX, y: startY, rotation: (i % 2 ? 8 : -8), scale: 0.92 });

        const at = i * 0.62;
        tl.to(el, { opacity: 1, x: centerX(230), y: 60, rotation: 0, scale: 1, duration: 0.55 }, at);
        const scan = el.querySelector('.doc__scan');
        tl.fromTo(scan, { opacity: 1, y: 0 }, { y: 120, duration: 0.5, ease: 'sine.inOut' }, at + 0.4);
        tl.set(scan, { opacity: 0 }, at + 0.9);
        tl.to(el, { x: centerX(230) + (i - 2) * 6, y: restY, rotation: restRot, scale: 0.84, duration: 0.5, ease: 'power2.in' }, at + 0.9);
        tl.to(el, { opacity: 0, duration: 0.25 }, at + 1.25);
        tl.call(setCount, [Math.round((i + 1) * (14 / DOCS.length))], at + 1.05);
        tl.to(dossier, { y: '+=4', duration: 0.12, yoyo: true, repeat: 1 }, at + 1.0);
      });

      const afterDocs = DOCS.length * 0.62 + 0.6;
      tl.call(() => setCount(14), null, afterDocs);

      // phase 2 — risk flags surface
      tl.call(() => { if (phaseRef.current) phaseRef.current.textContent = 'surfacing risks'; }, null, afterDocs + 0.1);
      const pos = flagPositions();
      flagEls.forEach((f, i) => {
        gsap.set(f, { left: pos[i].x, top: pos[i].y + 18, scale: 0.9 });
        tl.to(f, { opacity: 1, y: -18, scale: 1, duration: 0.5, ease: 'back.out(1.6)' }, afterDocs + 0.3 + i * 0.32);
        tl.call(setKFlags, [i + 1], afterDocs + 0.4 + i * 0.32);
      });

      // phase 3 — hold "ready for sign-off", then fade flags before the loop
      const ready = afterDocs + 0.3 + flagEls.length * 0.32 + 0.4;
      tl.call(() => { if (phaseRef.current) phaseRef.current.textContent = 'ready for sign-off'; }, null, ready);
      tl.to({}, { duration: 1.6 }, ready);
      tl.to(flagEls, { opacity: 0, y: '-=10', duration: 0.4, stagger: 0.08 }, ready + 1.6);
    }, sceneRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="stage">
      <div className="stage__status">
        <span className="live" />
        <span>Assembling dossier — <span className="stage__phase" ref={phaseRef}>reading documents</span></span>
      </div>

      <div className="scene" ref={sceneRef}>
        {DOCS.map((d, i) => (
          <div className="doc" key={d.name} ref={el => { docRefs.current[i] = el; }}>
            <div className="doc__row">
              <div className="doc__ico"><FileIcon /></div>
              <div>
                <div className="doc__type">{d.type}</div>
                <div className="doc__name">{d.name}</div>
              </div>
            </div>
            <div className="doc__lines">
              {lineWidths[i].map((w, j) => (
                <div className="doc__ln" key={j} style={{ width: `${w}%` }} />
              ))}
            </div>
            <div className="doc__scan" />
          </div>
        ))}

        <div className="dossier" ref={dossierRef}>
          <div className="dossier__back" />
          <div className="dossier__tab">Dossier</div>
          <div className="dossier__count num">
            <span ref={countRef}>0</span>
            <span className="lab">of 14 read</span>
          </div>
          <div className="dossier__front" />
          <div className="dossier__seam" />
          <div className="dossier__label">
            <div className="dossier__addr">Entrepotdok 26</div>
            <div className="dossier__meta">1018 AD Amsterdam · #DP-2026-0412</div>
          </div>
        </div>

        {FLAGS.map((f, i) => (
          <div className={`flag flag--${f.sev}`} key={f.label} ref={el => { flagRefs.current[i] = el; }}>
            <span className="fdot" />{f.label}<span className="fn">{f.ref}</span>
          </div>
        ))}
      </div>

      <div className="stage__foot">
        <div className="foot__i"><span className="foot__n num" ref={kFlagsRef}>0</span><span className="foot__l">flags surfaced</span></div>
        <div className="foot__i"><span className="foot__n num">14</span><span className="foot__l">document types</span></div>
        <div className="foot__i"><span className="foot__n num">~9 min</span><span className="foot__l">to sign-off</span></div>
      </div>
    </div>
  );
}
